import type {
  DataConnectionCredentials,
  S3ConnectionCredentials,
} from "common";
import {
  getDirForSsh,
  prepareBucketPath,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  BucketObject,
  getParentDirectory,
} from "@via/pages/data/data-sources/utils/helpers.tsx";

/**
 * The SSH and agent listings name every entry relative to "/" ("pi/u/test/agent55/"), while
 * the browser keeps the folder it shows as an absolute bucketName ("/pi/u/test"). Before #1000
 * the folder list request rewrote the component state into the root relative form in place;
 * it now lists a copy, so every path the browser builds starts from this view instead.
 */
export const sshBrowseView = (
  connection: DataConnectionCredentials
): DataConnectionCredentials => {
  const view = { ...connection };
  prepareBucketPath(view as S3ConnectionCredentials);
  return view;
};

/** The connection after opening a listed folder, or its parent for "..". */
export const sshOpenObject = (
  connection: DataConnectionCredentials,
  object: Pick<BucketObject, "id" | "name">
): DataConnectionCredentials => {
  const view = sshBrowseView(connection);
  view.prefix =
    object.id === ".."
      ? getParentDirectory(view.prefix)
      : object.name
        ? object.name
        : undefined;
  return { ...view, prefix: undefined, bucketName: getDirForSsh(view) };
};

/** Both upload doors read the file name as the absolute destination, so this ends in "/". */
export const sshUploadPrefix = (
  connection: DataConnectionCredentials | null | undefined
): string => "/" + ((connection && sshBrowseView(connection).prefix) ?? "");

/** The recursive listing request that adds a whole folder to the cart. */
export const sshFolderAddRequest = (
  connection: DataConnectionCredentials,
  folderName: string
): DataConnectionCredentials & { recursive: boolean } => ({
  ...sshBrowseView(connection),
  prefix: folderName,
  recursive: true,
});
